'use client';

import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useUser } from '@auth0/nextjs-auth0/client';
import { useParams, useRouter } from 'next/navigation';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { fetchBillingUsage } from '@/api/billing.api';
import { PlanLimitReachedDialog } from '@/components/ui/PlanLimitReachedDialog/PlanLimitReachedDialog';
import { UserRole } from '@/constants/enums';
import { isPlanLimitError, wouldExceedPlanLimit } from '@/lib/billing/plan-limit-client.util';
import { getAppRolesFromUser } from '@/utils/app-roles.util';
const USAGE_QUERY_KEY = ['billing', 'usage'];
const PlanLimitGuardContext = createContext(null);
export const PlanLimitGuardProvider = ({
  children
}) => {
  const params = useParams();
  const companySlug = params?.companySlug;
  const router = useRouter();
  const queryClient = useQueryClient();
  const {
    user
  } = useUser();
  const canManageBilling = useMemo(() => {
    const roles = getAppRolesFromUser(user);
    return roles.includes(UserRole.Admin) || roles.includes(UserRole.SuperAdmin);
  }, [user]);
  const [dialogKey, setDialogKey] = useState(null);
  const [upgradeLoading, setUpgradeLoading] = useState(false);
  const {
    data: usageSummary,
    refetch
  } = useQuery({
    queryKey: [...USAGE_QUERY_KEY, companySlug],
    queryFn: () => fetchBillingUsage(companySlug),
    enabled: Boolean(companySlug),
    staleTime: 30_000
  });
  const openPlanLimitDialog = useCallback(key => {
    setDialogKey(key);
  }, []);
  const invalidateUsage = useCallback(() => {
    void queryClient.invalidateQueries({
      queryKey: USAGE_QUERY_KEY
    });
  }, [queryClient]);
  const guardPlanLimit = useCallback(async (key, delta = 1) => {
    if (!companySlug) return true;
    let summary = usageSummary;
    if (!summary) {
      const refreshed = await refetch();
      summary = refreshed.data ?? null;
    }
    if (!summary) return true; // no subscription / failed fetch — let API decide
    if (wouldExceedPlanLimit(summary.limits, summary.usage, key, delta)) {
      openPlanLimitDialog(key);
      return false;
    }
    return true;
  }, [companySlug, usageSummary, refetch, openPlanLimitDialog]);
  const handleApiError = useCallback(err => {
    const matched = isPlanLimitError(err);
    if (!matched) return false;
    openPlanLimitDialog(matched.key);
    invalidateUsage();
    return true;
  }, [openPlanLimitDialog, invalidateUsage]);
  useEffect(() => {
    const id = axios.interceptors.response.use(res => res, error => {
      handleApiError(error);
      return Promise.reject(error);
    });
    return () => axios.interceptors.response.eject(id);
  }, [handleApiError]);
  const onDismiss = () => setDialogKey(null);
  const onUpgrade = () => {
    if (!companySlug || !canManageBilling) {
      onDismiss();
      return;
    }
    setUpgradeLoading(true);
    onDismiss();
    router.push(`/${companySlug}/settings?tab=billing&upgrade=1`);
    setUpgradeLoading(false);
  };
  const value = useMemo(() => ({
    openPlanLimitDialog,
    guardPlanLimit,
    handleApiError,
    invalidateUsage
  }), [openPlanLimitDialog, guardPlanLimit, handleApiError, invalidateUsage]);
  return <PlanLimitGuardContext.Provider value={value}>
            {children}
            <PlanLimitReachedDialog open={dialogKey != null} limitKey={dialogKey} canManageBilling={canManageBilling} contractBilling={usageSummary?.contractBilling} canUpgrade={!usageSummary?.contractBilling} upgradeLoading={upgradeLoading} onUpgrade={onUpgrade} onDismiss={onDismiss} />
        </PlanLimitGuardContext.Provider>;
};
export function usePlanLimitGuard() {
  const ctx = useContext(PlanLimitGuardContext);
  if (!ctx) {
    throw new Error('usePlanLimitGuard must be used within a PlanLimitGuardProvider');
  }
  return ctx;
}

/** Safe variant for optional provider (e.g. public pages). */
export function useOptionalPlanLimitGuard() {
  return useContext(PlanLimitGuardContext);
}